import React from "react";
import {
  FaTimes,
  FaStar,
  FaShoppingCart,
  FaUser,
  FaBoxOpen,
} from "react-icons/fa";

const LivePreview = ({ isOpen, onClose, data, endpoint }) => {
  const [isMounted, setIsMounted] = React.useState(false);
  const [showContent, setShowContent] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      // Small delay to ensure DOM is painted before triggering CSS transition
      setTimeout(() => setShowContent(true), 10);
    } else {
      setShowContent(false);
      // Wait for CSS transition to finish before unmounting
      const timer = setTimeout(() => setIsMounted(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isMounted && !isOpen) return null;

  // Render product card
  const renderProduct = (item) => (
    <div
      key={item.id || item._id || Math.random()}
      className="bg-white rounded-2xl shadow-sm hover:shadow-xl overflow-hidden border border-gray-100 transition-all duration-300 group flex flex-col"
    >
      <div className="h-56 bg-white relative p-6 flex items-center justify-center overflow-hidden">
        {item.image ? (
          <img
            src={item.image}
            alt={item.title}
            className="h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-500 ease-in-out"
          />
        ) : (
          <FaBoxOpen className="text-gray-200 text-7xl" />
        )}
        <div className="absolute top-4 right-4 bg-designColor text-white text-xs font-bold px-3 py-1 rounded-full shadow-md backdrop-blur-md">
          {item.category}
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1 border-t border-gray-50 bg-[#FAFAFA]">
        <h4
          className="font-bold text-primeColor text-sm line-clamp-2 leading-snug mb-2 group-hover:text-designColor transition-colors duration-300"
          title={item.title}
        >
          {item.title}
        </h4>

        <div className="flex justify-between items-end mt-auto pt-4 relative">
          <div className="flex flex-col">
            <span className="text-xs text-gray-400 line-through decoration-gray-300">
              ${(item.price * 1.2).toFixed(2)}
            </span>
            <span className="font-extrabold text-xl text-primeColor">
              ${item.price}
            </span>
          </div>

          {item.rating && (
            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200 shadow-[0_2px_10px_rgba(0,0,0,0.03)] pb-1">
              <FaStar className="text-yellow-400 text-xs" />
              <span className="text-xs font-bold text-gray-700">
                {typeof item.rating === "object"
                  ? item.rating.rate
                  : item.rating}
              </span>
              {typeof item.rating === "object" && item.rating.count && (
                <span className="text-[10px] text-gray-400 ml-1">
                  ({item.rating.count})
                </span>
              )}
            </div>
          )}
        </div>

        <button className="w-full mt-5 bg-primeColor hover:bg-designColor text-white font-titleFont font-medium py-2.5 rounded-lg transition-colors duration-300 shadow-md flex justify-center items-center gap-2 text-sm">
          <FaShoppingCart size={14} />
          Add to Cart
        </button>
      </div>
    </div>
  );

  // Render user card
  const renderUser = (item) => (
    <div
      key={item.id || item._id || Math.random()}
      className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-5 flex items-center gap-4 hover:shadow-lg transition-shadow duration-300"
    >
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
        <FaUser className="text-gray-400 text-2xl" />
      </div>
      <div className="flex flex-col">
        <h4 className="font-bold text-primeColor capitalize">
          {typeof item.name === "object"
            ? `${item.name?.firstname || ""} ${item.name?.lastname || ""}`
            : item.name}
        </h4>
        <p className="text-sm text-gray-500">{item.email}</p>
        <span className="text-xs text-gray-400 mt-1 capitalize">
          {item.address?.city}, {item.address?.street}
        </span>
      </div>
    </div>
  );

  // Render simple card for unmapped types (carts, orders, etc)
  const renderGeneric = (item) => (
    <div
      key={item.id || item._id || Math.random()}
      className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-4"
    >
      <div className="flex justify-between items-center mb-2 border-b border-gray-100 pb-2">
        <span className="font-bold text-gray-700">ID: {item.id}</span>
        {item.date && (
          <span className="text-xs text-gray-400">
            {new Date(item.date).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1 text-sm text-gray-600">
        {Object.entries(item)
          .slice(0, 4)
          .map(([key, value]) => {
            if (typeof value === "object") return null;
            return (
              <p key={key}>
                <span className="font-semibold capitalize">{key}:</span>{" "}
                {String(value)}
              </p>
            );
          })}
      </div>
    </div>
  );

  if (!isMounted) return null;

  // Logic to determine which card to render based on the endpoint string
  const renderContent = () => {
    // Extract actual data array or object (the API wraps the response in a `{ data: [...] }` object)
    const actualData = data?.data || data;

    // Determine if data is an array or single object
    const dataArray = Array.isArray(actualData) ? actualData : [actualData];

    if (
      !actualData ||
      dataArray.length === 0 ||
      (dataArray.length === 1 && Object.keys(dataArray[0]).length === 0)
    ) {
      return (
        <div className="p-8 text-center text-gray-500">
          No data available for preview.
        </div>
      );
    }

    if (endpoint.includes("products")) {
      return dataArray.map(renderProduct);
    } else if (endpoint.includes("user")) {
      return dataArray.map(renderUser);
    } else {
      return dataArray.map(renderGeneric);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] transition-opacity duration-500 ${
        showContent
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full md:w-[450px] bg-white shadow-2xl z-[110] transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${
          showContent ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-primeColor font-titleFont">
              Live UI Preview
            </h2>
            <p className="text-xs text-designColor font-semibold mt-1 uppercase tracking-wider">
              {endpoint}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:rotate-90 transition-all duration-300"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-[#f8f9fc] custom-scrollbar relative">
          {/* Subtle background pattern */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, black 1px, transparent 0)",
              backgroundSize: "24px 24px",
            }}
          ></div>

          <div className="relative z-10 flex flex-col gap-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePreview;
